import React from 'react';
import './Policy.css';

const PrivacyPolicy = () => {
  return (
    <div className="policy-page">
      <div className="container">
        <div className="policy-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: December 15, 2025</p>

          <div className="important-links" style={{background: '#f0f8ff', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '2px solid #007bff'}}>
            <h3 style={{marginTop: 0, color: '#007bff'}}>Related Legal Documents</h3>
            <p style={{marginBottom: '10px'}}><strong>This Privacy Policy works together with our:</strong></p>
            <ul style={{lineHeight: '1.8'}}>
              <li><strong>Terms of Use (EULA):</strong> <a href="https://landtracker.app/terms-of-use" target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'underline'}}>https://landtracker.app/terms-of-use</a></li>
            </ul>
            <p style={{fontSize: '14px', color: '#555', marginBottom: 0}}><em>Please read both documents to understand your rights and obligations when using Land Tracker.</em></p>
          </div>

          <section>
            <h2>Introduction</h2>
            <p>Land Tracker ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application (the "App") available on iOS (Apple App Store) and Android (Google Play Store).</p>
            <p>By using the App, you consent to the data practices described in this policy. If you do not agree with this policy, please do not use the App.</p>
          </section>

          <section>
            <h2>1. Information We Collect</h2>
            
            <h3>1.1 Personal Information</h3>
            <p>We collect information that you provide directly to us, including:</p>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, password, phone number (optional)</li>
              <li><strong>Profile Information:</strong> Profile photo, preferences, language settings</li>
              <li><strong>Property Information:</strong> Property addresses, ownership details, documents, photos, notes</li>
              <li><strong>Family Information:</strong> Names and relationships of family members you add to the family tree feature</li>
              <li><strong>Payment Information:</strong> Billing information is collected and processed by Apple Inc. (for iOS App Store purchases) or Google LLC (for Google Play purchases). We do not store, process, or have access to your credit card details, payment card numbers, or banking information. All payment data is handled securely by Apple or Google according to their respective privacy policies.</li>
              <li><strong>Subscription Information:</strong> We collect information about your subscription status, plan type, subscription start date, renewal date, and cancellation status to provide you with appropriate service access and features</li>
            </ul>

            <h3>1.2 Automatically Collected Information</h3>
            <ul>
              <li><strong>Location Data:</strong> GPS coordinates when using property mapping features (only when you grant permission)</li>
              <li><strong>Device Information:</strong> Device type, operating system version, unique device identifiers, mobile network information</li>
              <li><strong>Usage Data:</strong> Features used, time spent in the App, crash reports, performance data</li>
              <li><strong>Log Data:</strong> IP address, access times, app version, diagnostic logs</li>
            </ul>

            <h3>1.3 Information from Third Parties</h3>
            <ul>
              <li><strong>Apple Sign-In:</strong> If you sign in with Apple, we receive your name, email (or private relay email), and user identifier</li>
              <li><strong>Google Services:</strong> We use Google Maps API for location services and may receive anonymized usage data</li>
              <li><strong>Satellite Imagery:</strong> Property satellite images are retrieved from third-party providers</li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            
            <h3>2.1 Service Delivery</h3>
            <ul>
              <li>Create and maintain your account</li>
              <li>Provide property management features</li>
              <li>Process and manage subscriptions, including verifying subscription status, managing auto-renewals, and providing appropriate feature access based on your subscription tier</li>
              <li>Enable family sharing and collaboration features</li>
              <li>Provide AI-powered satellite monitoring</li>
              <li>Send notifications and alerts about your properties</li>
              <li>Verify your subscription status with Apple App Store or Google Play Store to ensure proper access to premium features</li>
            </ul>

            <h3>2.2 Communication</h3>
            <ul>
              <li>Send you service-related emails and notifications</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Send subscription renewal reminders</li>
              <li>Provide updates about new features (with your consent)</li>
            </ul>

            <h3>2.3 Improvement and Analytics</h3>
            <ul>
              <li>Analyze app usage to improve features and user experience</li>
              <li>Monitor and analyze trends and usage patterns</li>
              <li>Debug and fix technical issues</li>
              <li>Develop new features and services</li>
            </ul>

            <h3>2.4 Legal and Security</h3>
            <ul>
              <li>Comply with legal obligations and regulations</li>
              <li>Prevent fraud and unauthorized access</li>
              <li>Enforce our Terms of Use</li>
              <li>Protect our rights and the safety of users</li>
            </ul>
          </section>

          <section>
            <h2>3. Data Security</h2>
            <p>We implement industry-standard security measures to protect your data:</p>
            
            <h3>3.1 Encryption</h3>
            <ul>
              <li><strong>Data at Rest:</strong> AES-256 encryption for all stored data</li>
              <li><strong>Data in Transit:</strong> TLS 1.3/SSL encryption for all data transmission</li>
              <li><strong>Database Encryption:</strong> Encrypted database backups and snapshots</li>
            </ul>

            <h3>3.2 Access Controls</h3>
            <ul>
              <li>Multi-factor authentication for administrative access</li>
              <li>Role-based access control for internal staff</li>
              <li>Regular security audits and penetration testing</li>
              <li>Secure authentication with optional biometric login (Face ID, Touch ID, fingerprint)</li>
            </ul>

            <h3>3.3 Infrastructure Security</h3>
            <ul>
              <li>Secure cloud infrastructure with automatic backups</li>
              <li>Distributed denial-of-service (DDoS) protection</li>
              <li>Regular security updates and patches</li>
              <li>24/7 security monitoring</li>
            </ul>

            <h3>3.4 User Security Features</h3>
            <ul>
              <li>Password strength requirements</li>
              <li>Account activity monitoring</li>
              <li>Session timeout after inactivity</li>
              <li>Ability to remotely sign out of all devices</li>
            </ul>

            <p><strong>Important:</strong> While we implement robust security measures, no method of transmission over the internet or electronic storage is 100% secure. You are responsible for maintaining the confidentiality of your account credentials.</p>
          </section>

          <section>
            <h2>4. Data Sharing and Disclosure</h2>
            <p>We do not sell, rent, or share your personal information with third parties for their marketing purposes. We may share your information only in the following circumstances:</p>

            <h3>4.1 Service Providers</h3>
            <p>We may share data with trusted third-party service providers who assist in our operations:</p>
            <ul>
              <li><strong>Cloud Hosting:</strong> Secure data storage and backup services</li>
              <li><strong>Payment Processing:</strong> Apple Inc. and Google LLC for subscription billing</li>
              <li><strong>Analytics:</strong> Firebase Analytics (Google) for usage analytics</li>
              <li><strong>Satellite Imagery:</strong> Third-party providers for property monitoring</li>
              <li><strong>Email Services:</strong> For sending notifications and communications</li>
            </ul>
            <p>All service providers are bound by strict confidentiality agreements and are prohibited from using your data for any purpose other than providing services to us.</p>

            <h3>4.2 With Your Consent</h3>
            <ul>
              <li>When you explicitly authorize us to share information</li>
              <li>When sharing property information with family members you've added</li>
              <li>When exporting or sharing data you choose to share</li>
            </ul>

            <h3>4.3 Legal Requirements</h3>
            <p>We may disclose your information if required by law or in good faith belief that such action is necessary to:</p>
            <ul>
              <li>Comply with legal obligations, court orders, or government requests</li>
              <li>Enforce our Terms of Use and other policies</li>
              <li>Protect our rights, property, or safety</li>
              <li>Protect the rights, property, or safety of our users or the public</li>
              <li>Prevent fraud, security breaches, or illegal activity</li>
            </ul>

            <h3>4.4 Business Transfers</h3>
            <p>In the event of a merger, acquisition, bankruptcy, or sale of assets, your information may be transferred to the successor entity. We will notify you of any such change and provide options regarding your data.</p>

            <h3>4.5 Aggregated and De-identified Data</h3>
            <p>We may share aggregated, anonymized, or de-identified data that cannot reasonably be used to identify you for research, analytics, or business purposes.</p>
          </section>

          <section>
            <h2>5. Your Privacy Rights</h2>
            <p>You have the following rights regarding your personal data:</p>

            <h3>5.1 Access and Portability</h3>
            <ul>
              <li><strong>Access:</strong> Request a copy of all personal data we hold about you</li>
              <li><strong>Download:</strong> Export your property data, documents, and photos</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
            </ul>

            <h3>5.2 Correction and Updates</h3>
            <ul>
              <li>Update your account information in the app settings</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Request correction of data you cannot edit yourself</li>
            </ul>

            <h3>5.3 Deletion</h3>
            <ul>
              <li><strong>Account Deletion:</strong> Delete your entire account and associated data</li>
              <li><strong>Selective Deletion:</strong> Delete specific properties, documents, or photos</li>
              <li><strong>Right to be Forgotten:</strong> Request complete deletion of all your data</li>
            </ul>
            <p><em>Note: Some data may be retained for legal compliance or legitimate business purposes for a limited period after deletion.</em></p>

            <h3>5.4 Opt-Out Rights</h3>
            <ul>
              <li><strong>Marketing Communications:</strong> Unsubscribe from promotional emails</li>
              <li><strong>Push Notifications:</strong> Disable notifications in device settings</li>
              <li><strong>Location Tracking:</strong> Disable location permissions in device settings</li>
              <li><strong>Analytics:</strong> Request to opt-out of analytics tracking</li>
            </ul>

            <h3>5.5 Data Restriction</h3>
            <ul>
              <li>Restrict processing of your data in certain circumstances</li>
              <li>Object to processing based on legitimate interests</li>
              <li>Object to automated decision-making</li>
            </ul>

            <h3>5.6 How to Exercise Your Rights</h3>
            <p>To exercise any of these rights, contact us at privacy@landtracker.app. We will respond to your request within 30 days. For account deletion, you can also use the in-app account deletion feature in Settings.</p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>We retain your information for as long as necessary to provide services and fulfill the purposes outlined in this policy:</p>
            <ul>
              <li><strong>Active Accounts:</strong> Data is retained while your account is active</li>
              <li><strong>Deleted Accounts:</strong> Most data is deleted within 30 days of account deletion</li>
              <li><strong>Legal Requirements:</strong> Some data may be retained for up to 7 years for legal, tax, or audit purposes</li>
              <li><strong>Backup Systems:</strong> Deleted data may persist in backup systems for up to 90 days</li>
              <li><strong>Anonymized Data:</strong> Aggregated, anonymized data may be retained indefinitely for analytics</li>
            </ul>
          </section>

          <section>
            <h2>7. Cookies and Tracking Technologies</h2>
            <p>We use the following technologies to enhance your experience:</p>
            <ul>
              <li><strong>Session Cookies:</strong> To maintain your login session</li>
              <li><strong>Preference Cookies:</strong> To remember your settings and preferences</li>
              <li><strong>Analytics:</strong> Firebase Analytics to understand app usage (you can opt-out)</li>
              <li><strong>Crash Reporting:</strong> To identify and fix bugs</li>
            </ul>
            <p>You can control cookie preferences through your device settings. However, disabling certain cookies may limit app functionality.</p>
          </section>

          <section>
            <h2>8. Children's Privacy</h2>
            <p>Our service is not intended for users under 18 years of age. We do not knowingly collect personal information from children under 18.</p>
            <p>If we discover that we have collected information from a child under 18, we will delete that information immediately. If you believe we have collected information from a child, please contact us at privacy@landtracker.app.</p>
          </section>

          <section>
            <h2>9. International Data Transfers</h2>
            <p>Your data may be transferred to and processed in countries other than your country of residence, including the United States. These countries may have different data protection laws.</p>
            <p>We ensure that all international data transfers comply with applicable laws and implement appropriate safeguards such as:</p>
            <ul>
              <li>Standard contractual clauses approved by relevant authorities</li>
              <li>Adequacy decisions by regulatory bodies</li>
              <li>Encryption and security measures during transfer</li>
            </ul>
          </section>

          <section>
            <h2>10. California Privacy Rights (CCPA)</h2>
            <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
            <ul>
              <li><strong>Right to Know:</strong> What personal information we collect, use, disclose, and sell</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal information (we do not sell your data)</li>
              <li><strong>Right to Non-Discrimination:</strong> Equal service regardless of exercising your privacy rights</li>
            </ul>
            <p>To exercise these rights, contact us at privacy@landtracker.app or call 1-800-XXX-XXXX.</p>
          </section>

          <section>
            <h2>11. European Privacy Rights (GDPR)</h2>
            <p>If you are in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR):</p>
            <ul>
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Rights related to automated decision-making</li>
            </ul>
            <p>Our legal basis for processing your data includes:</p>
            <ul>
              <li><strong>Consent:</strong> When you provide explicit consent</li>
              <li><strong>Contract:</strong> To perform our contract with you (Terms of Use)</li>
              <li><strong>Legal Obligation:</strong> To comply with legal requirements</li>
              <li><strong>Legitimate Interests:</strong> To improve our services and prevent fraud</li>
            </ul>
          </section>

          <section>
            <h2>12. Platform-Specific Privacy Information</h2>
            
            <h3>12.1 iOS (Apple)</h3>
            <ul>
              <li>We comply with Apple's App Store Review Guidelines and App Tracking Transparency (ATT) framework</li>
              <li>Sign in with Apple provides privacy features like private email relay</li>
              <li>We request tracking permission before collecting identifiers for analytics</li>
              <li>Payment information is processed by Apple and subject to Apple's Privacy Policy</li>
            </ul>

            <h3>12.2 Android (Google)</h3>
            <ul>
              <li>We comply with Google Play Developer Program Policies</li>
              <li>We use Google Play Services for app functionality and analytics</li>
              <li>Payment information is processed by Google and subject to Google's Privacy Policy</li>
              <li>We follow Android's permission model for accessing device features</li>
            </ul>
          </section>

          <section>
            <h2>13. Third-Party Links and Services</h2>
            <p>The App may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies.</p>
            <p>Third-party services used in the App:</p>
            <ul>
              <li>Google Maps API (subject to Google Privacy Policy)</li>
              <li>Firebase Analytics (subject to Google Privacy Policy)</li>
              <li>Apple Services (subject to Apple Privacy Policy)</li>
            </ul>
          </section>

          <section>
            <h2>14. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes via:</p>
            <ul>
              <li>In-app notification</li>
              <li>Email to your registered address</li>
              <li>Notice on our website</li>
            </ul>
            <p>The "Last Updated" date at the top of this policy indicates when it was last revised. Continued use of the App after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2>15. Contact Us and Data Protection Officer</h2>
            <p>If you have questions, concerns, or complaints about this Privacy Policy or our data practices, please contact us:</p>
            <p><strong>Email:</strong> privacy@landtracker.app</p>
            <p><strong>Support:</strong> support@landtracker.app</p>
            <p><strong>Data Protection Officer:</strong> dpo@landtracker.app</p>
            <p><strong>Website:</strong> https://landtracker.app/privacy-policy</p>
            <p><strong>Mailing Address:</strong> Land Tracker Privacy Team, [Your Address]</p>
            
            <p>We will respond to all requests within 30 days. If you are not satisfied with our response, you have the right to lodge a complaint with your local data protection authority.</p>
          </section>

          <section className="acknowledgment">
            <h2>Your Consent</h2>
            <p><strong>By using Land Tracker, you acknowledge that you have read and understood this Privacy Policy and consent to the collection, use, and disclosure of your information as described herein.</strong></p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
